import { useEffect } from "react"
import { Link } from "react-router-dom"
import AssetPath from "../../AssetPath/AssetPath"
import { companyName, productName } from "../../config"
import "./Landing.scss"

const capabilities = [
  {
    kicker: "Inventory",
    title: "A fleet you can actually see",
    copy: "Windows, macOS, and Linux endpoints check in with an owner, an operating system, and a last-seen time. Devices that stop reporting stay visible instead of disappearing.",
  },
  {
    kicker: "Updates",
    title: "Patches and software in one queue",
    copy: "Approve Windows updates, deploy applications, and see which machines are still outstanding. Vulnerability findings and patch reports sit next to the devices they belong to.",
  },
  {
    kicker: "Protection",
    title: "Policies without a desk visit",
    copy: "Push configuration, local administrator passwords, and compliance rules from the console. Each organization keeps its own keys and its own policy set.",
  },
  {
    kicker: "Timing",
    title: "Work that waits for a quiet hour",
    copy: "Schedules and maintenance windows run installs and restarts when people are away. Deployments stay attached to the organization that requested them.",
  },
  {
    kicker: "Presence",
    title: "The desktop, not only the agent",
    copy: "Send a broadcast, set the corporate wallpaper, and remind someone who has been idle. Security training stays with the same company as the device.",
  },
  {
    kicker: "Tenancy",
    title: "One console, separate companies",
    copy: "A person signs in with an organization slug. Their devices, users, and policies never appear in another company’s view. Suspending an organization blocks sign-in and leaves the data in place.",
  },
]

const operations = [
  ["Schedules", "Recurring jobs for a whole organization"],
  ["Maintenance", "A window when a restart is acceptable"],
  ["Windows updates", "Approval, status, and what is still missing"],
  ["App deployment", "Software installed from the console"],
  ["Vulnerabilities", "Findings tied to the enrolled device"],
  ["Local admin", "Rotate and recover administrator passwords"],
  ["Broadcasts", "A message on the desktop"],
  ["Training", "Security training for that company’s people"],
]

const steps = [
  {
    n: "01",
    title: "Invite the organization",
    copy: "A platform administrator enters the company name and the first administrator’s email. Xpanse sends a setup link. Until that link is used, the organization stays invited.",
  },
  {
    n: "02",
    title: "The administrator sets a password",
    copy: "They open the link, choose a password, and sign in with the organization slug. The same email address can belong to a different person in another company.",
  },
  {
    n: "03",
    title: "Endpoints enroll into that company",
    copy: "The install command carries the organization. Agents report inventory, patches, and policy status into that tenant, not into a shared pool.",
  },
]

const platforms = [
  {
    name: "Windows",
    copy: "Updates, installed software, local administrator passwords, wallpaper, and maintenance windows.",
  },
  {
    name: "macOS",
    copy: "Inventory and agent check-in for Macs that belong to the same organization as the PCs.",
  },
  {
    name: "Linux",
    copy: "Servers and workstations enroll with the same organization slug as the rest of the fleet.",
  },
]

const sampleDevices = [
  { name: "FIN-LAPTOP-04", os: "Windows 11", state: "Checking in", ok: true },
  { name: "DESIGN-MAC-02", os: "macOS", state: "Checking in", ok: true },
  { name: "BUILD-LINUX-01", os: "Linux", state: "Needs attention", ok: false },
]

const LandingPage = () => {
  useEffect(() => {
    const previous = document.title
    document.title = `${productName} by ${companyName}`
    return () => {
      document.title = previous
    }
  }, [])

  let signedIn = false
  try {
    const user = JSON.parse(localStorage.getItem("user"))
    signedIn = Boolean(user?.success && user?.data?.user?._id)
  } catch (_) {
    signedIn = false
  }

  const consoleHref = signedIn ? "/dashboard" : "/signup"
  const consoleLabel = signedIn ? "Open console" : "Start free trial"

  return (
    <div className="landing">
      <header className="landing__bar">
        <div className="landing__nav">
          <Link to="/" className="landing__brand">
            <img src={AssetPath.brand.appIconCircle} alt="" />
            <span>
              <strong>{productName}</strong>
              <small>{companyName}</small>
            </span>
          </Link>
          <nav className="landing__nav-links">
            <a href="#platform">Platform</a>
            <a href="#operations">Operations</a>
            <a href="#organizations">Organizations</a>
            <a href="#coverage">Coverage</a>
            <Link to="/login">Sign in</Link>
            <Link to={signedIn ? "/dashboard" : "/signup"} className="landing__nav-cta">
              {signedIn ? "Open console" : "Start free trial"}
            </Link>
          </nav>
        </div>
      </header>

      <main>
        <section className="landing__hero">
          <div className="landing__hero-copy">
            <p className="landing__eyebrow">Endpoint management for many companies</p>
            <h1>Every company keeps its own devices.</h1>
            <p className="landing__lede">
              {productName} is how {companyName} runs inventory, patching, software,
              and compliance for more than one organization. Sign in with a company
              slug and the console shows only that fleet.
            </p>
            <div className="landing__actions">
              <Link to={consoleHref} className="landing__btn landing__btn--primary">
                {consoleLabel}
              </Link>
              {signedIn ? (
                <a href="#organizations" className="landing__btn">
                  How a company is added
                </a>
              ) : (
                <Link to="/login" className="landing__btn">
                  Sign in
                </Link>
              )}
            </div>
            {signedIn ? null : (
              <p className="landing__trial-note">30 days free. No credit card required.</p>
            )}
            <ul className="landing__facts">
              <li>Windows, macOS, and Linux</li>
              <li>PostgreSQL or SQL Server</li>
              <li>Invite, then enroll</li>
            </ul>
          </div>

          <aside className="landing__preview" aria-label="Illustration of the console">
            <div className="landing__preview-top">
              <img src={AssetPath.brand.appIconCircle} alt="" />
              <div>
                <strong>{productName}</strong>
                <span>Organization · acme</span>
              </div>
              <em>Sample</em>
            </div>
            <ul>
              {sampleDevices.map((device) => (
                <li key={device.name}>
                  <span className={device.ok ? "is-ok" : "is-warn"} />
                  <div>
                    <strong>{device.name}</strong>
                    <small>{device.os}</small>
                  </div>
                  <em>{device.state}</em>
                </li>
              ))}
            </ul>
            <p>An illustration of the device list. It is not a live fleet.</p>
          </aside>
        </section>

        <section id="platform" className="landing__section">
          <div className="landing__section-head">
            <p className="landing__eyebrow">Platform</p>
            <h2>What people do after they sign in</h2>
            <p className="landing__section-copy">
              The console is the operating surface for one organization at a time.
              Platform administrators can open another company, or invite one, without
              mixing the two fleets.
            </p>
          </div>
          <div className="landing__grid">
            {capabilities.map((item) => (
              <article key={item.title} className="landing__card">
                <p>{item.kicker}</p>
                <h3>{item.title}</h3>
                <p>{item.copy}</p>
              </article>
            ))}
          </div>
        </section>

        <section id="operations" className="landing__section">
          <div className="landing__section-head">
            <p className="landing__eyebrow">Operations</p>
            <h2>The work the console already covers</h2>
          </div>
          <div className="landing__ops">
            {operations.map(([title, copy]) => (
              <article key={title}>
                <h3>{title}</h3>
                <p>{copy}</p>
              </article>
            ))}
          </div>
        </section>

        <section id="organizations" className="landing__section landing__section--split">
          <div>
            <p className="landing__eyebrow">Organizations</p>
            <h2>A new company starts with an invite</h2>
            <p className="landing__section-copy">
              Nobody types a slug into an empty form and hopes the company exists.
              A platform administrator invites the company and its first administrator.
              The setup link is what turns that invite into a console they can use.
            </p>
            <Link to={consoleHref} className="landing__text-link">
              {signedIn ? "Open the console" : "Sign in as a platform administrator"}
            </Link>
          </div>
          <ol className="landing__steps">
            {steps.map((step) => (
              <li key={step.n}>
                <span>{step.n}</span>
                <div>
                  <h3>{step.title}</h3>
                  <p>{step.copy}</p>
                </div>
              </li>
            ))}
          </ol>
        </section>

        <section id="coverage" className="landing__section">
          <div className="landing__section-head">
            <p className="landing__eyebrow">Coverage</p>
            <h2>The same organization across the machines it owns</h2>
          </div>
          <div className="landing__platforms">
            {platforms.map((item) => (
              <article key={item.name}>
                <h3>{item.name}</h3>
                <p>{item.copy}</p>
              </article>
            ))}
          </div>
        </section>

        <section id="data" className="landing__section landing__data">
          <div>
            <p className="landing__eyebrow">Records</p>
            <h2>PostgreSQL or SQL Server, kept apart by organization</h2>
            <p className="landing__section-copy">
              Company records live in a relational database. {productName} creates the
              same organization tables on PostgreSQL or SQL Server. Device inventory
              stays with the organization that enrolled the agent.
            </p>
          </div>
          <div className="landing__engines">
            <article>
              <h3>PostgreSQL</h3>
              <p>For Linux and cloud hosts. The organization catalog is created when the API starts.</p>
            </article>
            <article>
              <h3>SQL Server</h3>
              <p>For Windows estates, including encrypted connections to an existing SQL Server.</p>
            </article>
          </div>
        </section>

        <section className="landing__close">
          <div>
            <h2>Open the console for your organization.</h2>
            <p>
              {signedIn
                ? `Open the console, or invite the next company from inside ${productName}.`
                : "Create the company yourself. The trial lasts 30 days and does not ask for a card."}
            </p>
          </div>
          <Link to={consoleHref} className="landing__btn landing__btn--primary">
            {consoleLabel}
          </Link>
        </section>
      </main>

      <footer className="landing__footer">
        <div>
          <strong>{productName}</strong>
          <span>A product of {companyName}</span>
        </div>
        <div className="landing__footer-links">
          <a href="#platform">Platform</a>
          <a href="#organizations">Organizations</a>
          <Link to="/privacy">Privacy</Link>
          <Link to="/terms">Terms</Link>
          <Link to="/login">Console</Link>
        </div>
        <p>© {new Date().getFullYear()} {companyName}. All rights reserved.</p>
      </footer>
    </div>
  )
}

export default LandingPage
